import {
  ICollectFormPayloadStructure,
  VGSCollectForm,
} from "@vgs/collect-js-react";
import React, { useEffect, useState } from "react";

import { loadVGSCollect } from "@vgs/collect-js";
import { Button } from "antd";
import { v4 } from "uuid";
import Spinner from "../components/Loading/Spinner";
import { isProd } from "../utils";
import "./CreditCardInputForm.css";
import { ErrorDisplay } from "./ErrorDisplay";

const { CardNumberField, CardExpirationDateField, CardSecurityCodeField } =
  VGSCollectForm;

interface CreditCardInputFormProps {
  cardActivationId: string;
  sessionId: string | null;
  onSuccess: () => void;
  externalError?: string | null;
  onClearExternalError: () => void;
}

const CreditCardInputForm: React.FC<CreditCardInputFormProps> = ({
  cardActivationId,
  sessionId,
  onSuccess,
  externalError,
  onClearExternalError,
}) => {
  const [isCollectLoaded, setCollectLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formKey, setFormKey] = useState(v4());
  const [cardholderName, setCardholderName] = useState("");
  const [zip, setZip] = useState("");
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const vaultId = isProd() ? "tnt9yj9ioof" : "tntcn8iwkyl";
  const environment = isProd() ? "live" : "sandbox";

  // Load VGS Collect script once
  useEffect(() => {
    loadVGSCollect({
      vaultId,
      environment,
      version: "3.2.2",
    }).then(() => setCollectLoaded(true));
  }, []);

  if (!isCollectLoaded) return null; // Wait until script loads

  const VGSCollectFieldStyles = {
    fontSize: "14px",
    fontFamily: "Arial, sans-serif",
    color: "#374151",
    backgroundColor: "transparent",
    border: "none",
    padding: "10px 40px 10px 16px",
    height: "42px",
    minHeight: "42px",
    lineHeight: "20px",
    boxSizing: "border-box",
    width: "100%",
    "&::placeholder": {
      color: "#9ca3af",
    },
    "&:focus": {
      outline: "none",
    },
  };

  return (
    <div className="w-full min-h-screen flex justify-center items-center">
      <div className="w-[500px]">
        <VGSCollectForm
          vaultId={vaultId}
          environment={environment}
          action={`/supplier_enablement/card_activations/${cardActivationId}`}
          key={formKey}
          submitParameters={{
            method: "PATCH",
            // JSON request body generated on the form submission including custom parameters
            // https://www.verygoodsecurity.com/docs/vgs-collect/js/integration#form-submit
            data: (fields: ICollectFormPayloadStructure) => {
              setIsLoading(true);
              setErrorMessage(null);
              const payload: any = {
                session_id: sessionId,
                cards: [
                  {
                    pan: fields["card-number"],
                    cvc: fields["card-security-code"],
                    exp_date: fields["exp-date"],
                    cardholder_name: cardholderName,
                    zip: zip,
                  },
                ],
              };

              return payload;
            },
          }}
          onSubmitCallback={(status, response) => {
            setIsLoading(false);

            // Check if the request was successful (200 status)
            if (status === 200) {
              setSubmissionSuccess(true);
              setErrorMessage(null);
              // Call the success callback to notify parent
              onSuccess();
            } else {
              // Handle non-200 responses
              const errorMsg =
                response?.error_message ||
                `Request failed with status ${status}`;
              setErrorMessage(errorMsg);
            }
          }}
          onErrorCallback={(errors) => {
            console.error("Card activation submission error:", errors);
            setIsLoading(false);

            // Extract error message if available
            const errorMsg =
              errors?.[0]?.message ||
              "Failed to submit payment. Please try again.";
            setErrorMessage(errorMsg);
          }}>
          {/* Form Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Please enter payment information before proceeding:
            </h2>
          </div>

          <div className="space-y-5">
            {/* Cardholder Name Input */}
            <div>
              <label
                htmlFor="cardholder-name"
                className="block text-sm font-semibold text-gray-700 mb-2">
                Cardholder Name
              </label>
              <input
                id="cardholder-name"
                type="text"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                style={{
                  fontSize: "14px",
                  fontFamily: "Arial, sans-serif",
                }}
              />
            </div>

            {/* ZIP Code Input */}
            <div>
              <label
                htmlFor="zip-code"
                className="block text-sm font-semibold text-gray-700 mb-2">
                ZIP Code
              </label>
              <input
                id="zip-code"
                type="text"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="12345"
                maxLength={10}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                style={{
                  fontSize: "14px",
                  fontFamily: "Arial, sans-serif",
                }}
              />
            </div>
          </div>

          {/* Card Information Section */}
          <div className="mt-6 pt-5 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              Card Information
            </h3>

            <div className="space-y-4">
              {/* Card Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Card Number
                </label>
                <div className="vgs-field-wrapper">
                  <CardNumberField
                    validations={["required", "validCardNumber"]}
                    showCardIcon={{
                      right: "1rem",
                    }}
                    name="card-number"
                    css={VGSCollectFieldStyles}
                    placeholder="1234 5678 9012 3456"
                  />
                </div>
              </div>

              {/* Expiration and CVC in a row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Expiration Date
                  </label>
                  <div className="vgs-field-wrapper">
                    <CardExpirationDateField
                      validations={["required", "validCardExpirationDate"]}
                      yearLength={2}
                      css={VGSCollectFieldStyles}
                      name="exp-date"
                      placeholder="MM / YY"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    CVC
                  </label>
                  <div className="vgs-field-wrapper">
                    <CardSecurityCodeField
                      name="card-security-code"
                      validations={["required", "validCardSecurityCode"]}
                      css={VGSCollectFieldStyles}
                      placeholder="123"
                      showCardIcon={{
                        right: "1rem",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Status Messages and Actions */}
          <div className="mt-6 space-y-4">
            {/* Error Display */}
            <ErrorDisplay
              errorMessage={errorMessage || externalError || null}
            />
            <div className="flex gap-3 pt-2">
              <Button
                disabled={isLoading || submissionSuccess}
                htmlType={"submit"}
                onClick={() => {
                  setErrorMessage(null);
                  onClearExternalError();
                }}
                className="flex-1 submit-button-purple font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ height: "auto" }}>
                <span className="flex items-center justify-center gap-2">
                  {isLoading && <Spinner />}
                  <span>
                    {isLoading ? "Submitting..." : "Submit card details"}
                  </span>
                </span>
              </Button>
            </div>
          </div>
        </VGSCollectForm>
      </div>
    </div>
  );
};

export default CreditCardInputForm;
